import { DeleteOutlined } from "@ant-design/icons"
import { Flex, Tag, Typography } from "antd"
import type { ItemType } from "antd/es/menu/interface"
import type { ColumnsType } from "antd/es/table"
import React from "react"

import Icon from "../../../../../../../../Icon"
import { empty } from "../../../../../../../../constants/placeholders"
import {
  type Attachment,
  AttachmentModels
} from "../../../../../../../Album/types/attachment.types.ts"
import { AttachmentsController } from "../../../../../../components/AttachmentsController.tsx"
import { RowActionsMenu } from "../../../../../../../../components/EntityTable/RowActionsMenu.tsx"
import { type ProjectChecklistItem } from "../../../../../../types/project-checklists.types.tsx"
import {
  CHECKLIST_ITEM_PRIORITIES_MAP,
  CHECKLIST_ITEM_STATUSES_MAP,
  type ChecklistItemStatuses
} from "../../../../../constants/checklist-item-constants.ts"
import { ProjectViewChecklistsListCheckbox } from "./ProjectViewChecklistsListCheckbox.tsx"

const { Text } = Typography

// Shared with the checklist edit rows, which match their cells to these
// columns by key rather than by position. `is_done` is hidden when the user
// cannot edit, and buildEditRowCells simply skips a column that is absent.
export const CHECKLIST_COLUMN_KEYS = {
  isDone: "is_done",
  name: "name",
  projectTypeDisplay: "project_type_display",
  projectPhaseName: "project_phase_name",
  priorityDisplay: "priority_display",
  assignedToName: "assigned_to_name",
  startDate: "start_date",
  endDate: "end_date",
  completedAt: "completed_at",
  status: "status",
  attachments: "attachments"
} as const

interface GetColumnsArg {
  currentUserId: string
  disabled: boolean
  isLocked: boolean
  isAdding?: boolean
  //
  deleteAllowed?: boolean
  editAllowed?: boolean
  uploadFileAllowed?: boolean
  deleteFileAllowed?: boolean
  deleteForeignFileAllowed?: boolean
  //
  onToggleDone: (id: string, isDone: boolean) => void
  onSelectRow: (record: ProjectChecklistItem, action: string) => void
}

export const getColumns = ({
  currentUserId,
  disabled,
  isLocked,
  isAdding,
  editAllowed,
  deleteAllowed,
  uploadFileAllowed,
  deleteFileAllowed,
  deleteForeignFileAllowed,
  onToggleDone,
  onSelectRow
}: GetColumnsArg): ColumnsType<ProjectChecklistItem> => [
  {
    title: "",
    key: "is_done",
    width: 30,
    hidden: !editAllowed,
    align: "center",
    render: (_, record) => (
      <ProjectViewChecklistsListCheckbox
        checked={record.is_done}
        onChange={() => onToggleDone(record?.id, !record?.is_done)}
        disabled={disabled}
      />
    )
  },
  {
    title: "Task Details",
    dataIndex: "name",
    key: "name",
    width: 180,
    render: (text, record) => <Text delete={record.is_done}>{text}</Text>
  },
  {
    title: "Project Type",
    dataIndex: "project_type_display",
    key: "project_type_display",
    width: 200,
    render: (value: string | null) => value || empty
  },
  {
    title: "Milestone",
    dataIndex: "project_phase_name",
    key: "project_phase_name",
    width: 170,
    render: (phaseName: string) => (
      <Tag className="m-0 max-w-full">
        <Text ellipsis={{ tooltip: phaseName }} className="text-xs block">
          {phaseName}
        </Text>
      </Tag>
    )
  },
  {
    title: "Priority",
    dataIndex: "priority_display",
    key: "priority_display",
    width: 110,
    render: (priorityDisplay: string, record) => {
      const config = CHECKLIST_ITEM_PRIORITIES_MAP?.[record?.priority]
      return <Tag style={config?.style}>{priorityDisplay || empty}</Tag>
    }
  },
  {
    title: "Assigned",
    dataIndex: "assigned_to_name",
    key: "assigned_to_name",
    width: 250,
    render: (text, record) => {
      const withoutName = !text && record?.assigned_to

      if (withoutName) return "No name provided"

      return <Text>{text || empty}</Text>
    }
  },
  {
    title: "Start Date",
    dataIndex: "start_date",
    key: "start_date",
    width: 140,
    render: (text) => <Text>{text || empty}</Text>
  },
  {
    title: "End Date",
    dataIndex: "end_date",
    key: "end_date",
    width: 140,
    render: (text) => <Text>{text || empty}</Text>
  },
  {
    title: "Actual End",
    dataIndex: "completed_at",
    key: "completed_at",
    width: 170,
    render: (text) => <Text>{text || empty}</Text>
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 135,
    render: (status: ChecklistItemStatuses, record) => {
      const statusData = CHECKLIST_ITEM_STATUSES_MAP?.[status]

      return <Tag style={statusData?.style}>{record?.status_display}</Tag>
    }
  },
  {
    title: "Attachments",
    key: "attachments",
    dataIndex: "attachments",
    width: 100,
    align: "center",
    render: (attachments: Attachment[], record) => (
      <AttachmentsController
        currentUserId={currentUserId}
        addAllowed={editAllowed}
        deleteAllowed={deleteFileAllowed}
        deleteForeignAllowed={deleteForeignFileAllowed}
        project_id={record?.project_id}
        model_type={AttachmentModels.CHECKLIST_ITEM}
        object_id={record?.id}
        attachments={attachments}
      />
    )
  },
  {
    title: "Actions",
    width: 90,
    align: "center",
    fixed: "right",
    hidden: !deleteAllowed && !editAllowed && !isAdding,
    render: (_, record) => {
      if (!deleteAllowed && !editAllowed) return null
      return (
        <Flex gap={"small"} justify={"center"} className={"w-20 mx-auto"}>
          <RowActionsMenu
            disabled={isLocked}
            items={
              [
                {
                  key: "edit_row",
                  label: "Edit",
                  hidden: !editAllowed,
                  icon: <Icon type={"Edit"} />,
                  onClick: () => onSelectRow(record, "edit_row")
                },
                {
                  key: "delete_row",
                  label: "Delete",
                  hidden: !deleteAllowed,
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => onSelectRow(record, "delete_row")
                }
              ] as Array<ItemType & { hidden: boolean }>
            }
          />
        </Flex>
      )
    }
  }
]
